import { useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Landmark, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const SOURCES = [
    { v: "cash", label: "Cash" }, { v: "debt", label: "Debt-funded" },
    { v: "pe_backed", label: "PE-backed" }, { v: "strategic", label: "Strategic / balance sheet" },
    { v: "mixed", label: "Mixed" },
];

/** Buyer funding capacity + proof-of-funds verification. Verifier name/date are server-set. */
export default function FundingPanel({ contact, onSaved }) {
    const [source, setSource] = useState(contact.funding_source || "");
    const [capital, setCapital] = useState(contact.available_capital ?? "");
    const [docId, setDocId] = useState(contact.pof_document_id || "");
    const [saving, setSaving] = useState(false);

    const save = async (extra = {}) => {
        setSaving(true);
        try {
            await api.patch(`/contacts/${contact.id}`, {
                ...(source ? { funding_source: source } : {}),
                ...(capital !== "" ? { available_capital: parseFloat(capital) } : {}),
                ...(docId ? { pof_document_id: docId.trim() } : {}),
                ...extra,
            });
            toast.success("Funding details saved");
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally { setSaving(false); }
    };

    const verified = !!contact.pof_verified;
    return (
        <div className="border border-border rounded-md bg-white p-5" data-testid="funding-panel">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold tracking-tight flex items-center gap-2">
                    <Landmark className="w-4 h-4 text-gold" /> Funding capacity
                </h3>
                {verified ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700" data-testid="pof-verified-badge">
                        <ShieldCheck className="w-3.5 h-3.5" /> Proof of funds verified
                        {contact.pof_verified_by ? ` by ${contact.pof_verified_by}` : ""}
                        {contact.pof_verified_at ? ` · ${new Date(contact.pof_verified_at).toLocaleDateString("en-GB")}` : ""}
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><ShieldAlert className="w-3.5 h-3.5" /> Not verified</span>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Funding source</label>
                    <Select value={source} onValueChange={setSource}>
                        <SelectTrigger data-testid="funding-source-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>{SOURCES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Available capital</label>
                    <Input type="number" className="font-mono" value={capital} onChange={(e) => setCapital(e.target.value)} data-testid="funding-capital-input" />
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">PoF document ID</label>
                    <Input value={docId} onChange={(e) => setDocId(e.target.value)} placeholder="Data-room document ID" />
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={() => save()} disabled={saving} data-testid="funding-save-btn">Save</Button>
                    <Button
                        onClick={() => save({ pof_verified: !verified })} disabled={saving}
                        className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="pof-verify-btn">
                        {verified ? "Unverify" : "Mark verified"}
                    </Button>
                </div>
            </div>
            {contact.available_capital != null && (
                <p className="text-xs text-muted-foreground mt-3">Stated capital: <span className="font-mono text-navy">{fmtMoney(contact.available_capital, "GBP")}</span> — bids above this are flagged on the deal's Offers tab.</p>
            )}
        </div>
    );
}
